import { BaseInputTemplateProps, FieldTemplateProps } from '@rjsf/utils'
import BaseInputTemplate from '@rjsf/core/lib/components/templates/BaseInputTemplate'
import classNames from 'classnames'
import { Col, Row } from 'react-bootstrap'
import { Controller, useFormContext } from 'react-hook-form'
import RJSFForm, { FormProps, IChangeEvent } from '@rjsf/core'
import validator from '@rjsf/validator-ajv8'
import { CustomCheckboxWidget } from './CustomFormFields/CustomCheckboxWidget'
import { CustomTextWidget } from './CustomFormFields/CustomTextWidget'
import { CustomPercentageWidget } from './CustomFormFields/CustomPercentageWidget'
import './formFills.scss'
import { CustomSingleChoiceWidget } from './CustomFormFields/CustomSingleChoiceView'
import { CustomMultipleChoiceWidget } from './CustomFormFields/CustomMultipleChoiceView'
import React, { useCallback, useRef, useState } from 'react'
import { useAppDispatch } from '../../../../../store/hooks'
import { setResultExplainerResult, setResultExplainerResultContext } from '../../../../../store/meetingSummary/meetingSupport'
import { MissingScopeAction } from '../../../../../components/ScopedBlock/models'
import { Button } from '../../../../../components/Button/Button'
import { ScopedBlock } from '../../../../../components/ScopedBlock/ScopedBlock'
import { displaySidebar } from '../../../../../store/meetingSummary/display'
import { SidebarTitle, SidebarType } from '../Sidebar/models'
import { parseMetadata } from '../utils'
import { AIAgentResultCitationTooltip } from '../../../../../views/AIAgent/AIAgentResultCitations/AIAgentResultCitations'
import { useOutsideClick } from '../../../../../common/useOutsideClick'
import classnames from 'classnames'
import { Tooltip } from '../../../../../components/Tooltip/Tooltip'
import { CustomWidgetProps } from './FormCitation/FormCitation'
import { TrackEvent, useTracking } from '../../common/tracking'

interface FormFieldViewProps {
  isEditing: boolean
  disabled: boolean
  label: string
  schema: {
    [key: string]: any
  }
  formKey: string
  uiSchema: { [key: string]: any }
  setValidatorSucceeded: (value: boolean) => void
  onExecuteCitation: () => Promise<void>
  isCitationLoading: boolean
}

interface CitationButtonProps {
  disabled: boolean
  isCitation: boolean
  onClick: () => void
  isLoading: boolean
}

type WidgetConfig = {
  pattern: string
  customPlaceholder: string
}

const WIDGET_CONFIG: Record<string, WidgetConfig> = {
  CurrencyWidget: {
    pattern: '^\\d+(\\.\\d{1,2})? [A-Z]{3}$',
    customPlaceholder: 'e.g., 50000.00 USD',
  },
  USDateInputWidget: {
    pattern: '^(0[1-9]|1[0-2])/(0[1-9]|[12][0-9]|3[01])/\\d{4}$',
    customPlaceholder: 'MM/DD/YYYY',
  },
  ITINInputWidget: {
    pattern: '^\\d{3}-\\d{2}-\\d{4}$',
    customPlaceholder: 'XXX-XX-XXXX',
  },
  SSNInputWidget: {
    pattern: '^\\d{3}-\\d{2}-\\d{4}$',
    customPlaceholder: 'XXX-XX-XXXX',
  },
  PhoneNumberWidget: {
    pattern: '^[0-9+\\-()]+$',
    customPlaceholder: 'e.g. (212) 456-7890',
  },
}

const CustomFieldTemplate = (props: FieldTemplateProps) => {
  return <>{props.children}</>
}

const CustomUnsupportedFieldTemplate = () => {
  return <></>
}

const CustomBaseInputTemplate = (props: BaseInputTemplateProps) => {
  const { isEditing, citationButton, fieldContainerClass } = props.options as CustomWidgetProps

  return (
    <div className={fieldContainerClass}>
      <BaseInputTemplate
        {...props}
        className={classNames('form-control', { 'disabled-contact-information-field border-0': props.disabled })}
        placeholder={'No relevant information detected'}
      />
      {!isEditing && props.value ? citationButton : null}
    </div>
  )
}

const CitationButton = React.forwardRef<HTMLDivElement, CitationButtonProps>((props, ref) => {
  const { disabled, isCitation, onClick, isLoading } = props

  return (
    <ScopedBlock
      scope={{
        scope: 'aifeature:execute',
        superScope: ['aifeature:admin'],
      }}
      missingScopeAction={MissingScopeAction.DISABLE}
    >
      {isCitation ? (
        <div ref={ref}>
          <Button variant='darkGhost' onClick={onClick} className='button-small zocks-py-0.25 zocks-px-0.75 zocks-border-color-gray-300' disabled={disabled}>
            {isLoading && (
              <i
                className={classnames('bi bi-arrow-repeat', {
                  'spin-faster': isLoading,
                })}
              ></i>
            )}
            Source
          </Button>
        </div>
      ) : (
        <Button variant='darkGhost' onClick={onClick} className='button-small zocks-py-0.25 zocks-px-0.75 zocks-border-color-gray-300'>
          Explain
        </Button>
      )}
    </ScopedBlock>
  )
})

export const FormFieldView: React.FC<FormFieldViewProps> = ({
  label,
  isEditing,
  disabled,
  schema,
  formKey,
  uiSchema,
  setValidatorSucceeded,
  onExecuteCitation,
  isCitationLoading,
}) => {
  const { control, getValues, watch } = useFormContext()
  const { includeAdditionalSources = true } = parseMetadata(getValues('metadata')) ?? {}
  const dispatch = useAppDispatch()
  const [showCitationModal, setShowCitationModal] = useState<boolean>(false)
  const tooltipRef = useRef<HTMLDivElement>(null)
  const ref = useRef<HTMLDivElement>(null)
  const allCitations = watch('citations')
  const citationForLabel = allCitations?.citations?.[label] ?? null
  const { tracked } = useTracking()

  const fieldContainerClass = classNames({
    'zocks-py-0.25 zocks-px-0.75 d-flex align-items-center zocks-bg-color-gray-200 zocks-text-gray-900 zocks-border-0.25': !isEditing,
  })

  const hideTooltip = useCallback(() => setShowCitationModal(false), [])

  useOutsideClick(tooltipRef, hideTooltip)

  const _handleResultExplainerClicked = useCallback(() => {
    const resultToExplain = getValues('formResultToDisplay')[label]
    dispatch(setResultExplainerResult(`${schema.title} : ${resultToExplain}`))
    dispatch(setResultExplainerResultContext(schema.description))
    dispatch(
      displaySidebar({
        type: SidebarType.RESULT_EXPLAINER,
        title: SidebarTitle.RESULT_EXPLAINER,
        icon: 'bi-journal-text',
      }),
    )
  }, [dispatch, getValues, label, schema.description, schema.title])

  const handleResultExplainerClicked = tracked(_handleResultExplainerClicked, {
    eventName: TrackEvent.FORM_RESULT_EXPLAINED,
    eventDetails: {
      resultId: getValues('formResultToDisplay').resultId,
    },
  })

  const handleCitationButtonClicked = useCallback(async () => {
    if (includeAdditionalSources) {
      try {
        await onExecuteCitation()
      } catch (error) {
        console.error('Citation execution failed:', error)
      } finally {
        setShowCitationModal(true)
      }
    } else {
      handleResultExplainerClicked()
    }
  }, [includeAdditionalSources, onExecuteCitation, handleResultExplainerClicked])

  const citationButton = getValues('edited') ? (
    <Tooltip className='paragraph-small' title='The form was manually edited, so the reference may not be accurate.' placement='top' offset={[0, 10]}>
      <CitationButton
        isCitation={includeAdditionalSources}
        disabled={isCitationLoading || (allCitations && !citationForLabel)}
        onClick={handleCitationButtonClicked}
        isLoading={isCitationLoading}
        ref={ref}
      />
    </Tooltip>
  ) : (
    <CitationButton
      isCitation={includeAdditionalSources}
      disabled={isCitationLoading || (allCitations && !citationForLabel)}
      onClick={handleCitationButtonClicked}
      isLoading={isCitationLoading}
      ref={ref}
    />
  )

  const key = uiSchema?.['ui:widget'] as keyof typeof WIDGET_CONFIG
  const config = key ? WIDGET_CONFIG[key] : null

  const finalUiSchema = {
    ...uiSchema,
    'ui:options': {
      ...(config && {
        pattern: config.pattern,
        customPlaceholder: config.customPlaceholder,
      }),
      isEditing,
      citationButton,
      fieldContainerClass,
      setValidatorSucceeded,
    },
  }

  const formProps: FormProps = {
    validator,
    schema,
    showErrorList: false,
    uiSchema: {
      'ui:submitButtonOptions': { norender: true },
      'ui:emptyValues': null,
      ...finalUiSchema,
    },
    templates: {
      FieldTemplate: CustomFieldTemplate,
      UnsupportedFieldTemplate: CustomUnsupportedFieldTemplate,
      BaseInputTemplate: CustomBaseInputTemplate,
    },
    widgets: {
      CheckboxWidget: CustomCheckboxWidget,
      CurrencyWidget: CustomTextWidget,
      USDateInputWidget: CustomTextWidget,
      PercentageWidget: CustomPercentageWidget,
      ITINInputWidget: CustomTextWidget,
      SSNInputWidget: CustomTextWidget,
      PhoneNumberWidget: CustomTextWidget,
      SingleChoiceWidget: CustomSingleChoiceWidget,
      MultipleChoiceWidget: CustomMultipleChoiceWidget,
    },
  }

  return (
    <>
      <Row className='mb-3'>
        <Col sm={2}>
          <div className='bold me-1'>{schema.title}</div>
        </Col>
        <Col>
          <Controller
            name={`formResultToDisplay.${label}`}
            control={control}
            render={({ field }) => (
              <RJSFForm
                key={formKey}
                {...formProps}
                formData={field.value}
                disabled={disabled || !isEditing}
                onChange={(e: IChangeEvent) => {
                  field.onChange(e.formData ?? null)
                }}
              />
            )}
          />
        </Col>
        {citationForLabel && !isCitationLoading && (
          <AIAgentResultCitationTooltip citations={citationForLabel} show={showCitationModal} target={ref.current} tooltipRef={tooltipRef} />
        )}
      </Row>
    </>
  )
}
